import type React from 'react'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Texto } from '@gravitate-js/excalibrr'
import { ApartmentOutlined } from '@ant-design/icons'
import { TreeSelect, type TreeSelectProps } from 'antd'
import type { ICellEditorParams } from 'ag-grid-community'
import { DefaultOptionType } from 'antd/lib/select'

type TreeListItem = string | { title: string; value: string; key: string }

type Props = ICellEditorParams & {
  data: any
  supply_zones: { parent: string[] }
  treeData: TreeSelectProps['treeData']
  defaultList?: string[]
  width?: number
}

export const InNetworkTreeSelectEditor: React.FC<Props> = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value)
  const [_, setSearchValue] = useState('')
  const refInput = useRef(null)

  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },
      isPopup() {
        return true
      },
      isCancelBeforeStart() {
        return false
      },
      isCancelAfterEnd() {
        return false
      },
    }
  })

  const supplyZones = useMemo(() => Object.keys(props?.supply_zones || {}), [props?.supply_zones])

  const handleChange = (selection) => {
    const selectedTerminals = selection.map((value) => {
      if (supplyZones.includes(value)) {
        const terminals = props.supply_zones?.[value]
        return terminals
      }
      return value
    })

    setValue(selectedTerminals.flat())
    setSearchValue('')
  }
  const defaultList = useMemo(() => {
    if (props.defaultList) return props.defaultList

    return props.data.sector_in_network_terminal_names?.length
      ? props.data.sector_in_network_terminal_names
      : props.data.market_in_network_terminal_names || []
  }, [props.data, props.defaultList])
  const treeData = useMemo(() => {
    if (props.treeData) return props.treeData

    const supplyZonesArray = Object.entries(props?.supply_zones)
    const list = props.data.sector_in_network_terminal_names?.length
      ? props.data.sector_in_network_terminal_names
      : props.data.market_in_network_terminal_names || []
    return supplyZonesArray.map(([parent, children]) => {
      return {
        title: parent,
        value: parent,
        key: parent,
        children: children?.map((child: TreeListItem) => {
          if (list.includes(child)) {
            return {
              title: (
                <Texto>
                  {typeof child === 'string' ? child : child.title}{' '}
                  <ApartmentOutlined style={{ color: 'var(--theme-color-2)' }} className='ml-1' />
                </Texto>
              ),
              value: typeof child === 'string' ? child : child.value,
              key: typeof child === 'string' ? child : child.key,
            }
          }
          return {
            title: typeof child === 'string' ? child : child.title,
            value: typeof child === 'string' ? child : child.value,
            key: typeof child === 'string' ? child : child.key,
          }
        }),
      }
    })
  }, [props.data, props.supply_zones, props.treeData])

  useEffect(() => {
    if (!value?.length) setValue(defaultList)
  }, [])

  return (
    <TreeSelect
      ref={refInput}
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeData={treeData}
      allowClear
      showSearch
      style={{ maxWidth: props.width || '200px', minWidth: props.width || '200px' }}
      placeholder='Select In Market Terminals'
      onChange={handleChange}
      value={value}
      className='mr-2'
      maxTagCount='responsive'
      multiple
      treeDefaultExpandAll
      treeCheckable
      showArrow={false}
      defaultOpen
      onClear={() => setValue(defaultList)}
      onSearch={(value) => setSearchValue(value)}
    />
  )
})
